import React, { useRef, useContext, useState, useEffect } from 'react';
import MenuDivider from 'components/MenuDivider';
import ColorfulButton from 'components/ColorfulButton';
import styled from '@emotion/styled/macro';
import { useSelector, useDispatch } from 'react-redux';
import { postSelectors, postActions } from 'redux/post';
import PostContext from 'components/Post/PostContext';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { isMobile, copyToClipboard } from 'helpers';
import history from 'providers/history';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import useConfirm from 'components/Confirmation/useConfirm';
import EditPostDialog from 'containers/Post/PostForm/EditPostDialog';
import { pollActions, pollSelectors } from 'redux/poll';
import Grid from 'components/Grid';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import NotificationsNoneRoundedIcon from '@mui/icons-material/NotificationsNoneRounded';
import NotificationsOffRoundedIcon from '@mui/icons-material/NotificationsOffRounded';
import TrashIcon from 'components/Icons/Trash';
import EditIcon from 'components/Icons/Edit';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import PublishedWithChangesRoundedIcon from '@mui/icons-material/PublishedWithChangesRounded';
import DownloadIcon from 'components/Icons/Download';
import ErrorIcon from '@mui/icons-material/Error';
import ReportOffRoundedIcon from '@mui/icons-material/ReportOffRounded';
import PinIcon from 'components/Icons/Pin';
import UnpinIcon from 'components/Icons/Unpin';
import LinkRoundedIcon from '@mui/icons-material/LinkRounded';
import ShareRoundedIcon from '@mui/icons-material/ShareRounded';
import { snackbarActions } from 'redux/snackbar';
import config from 'config';
import useRouterLocationState from 'hooks/useRouterLocationState';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import LockIcon from '@mui/icons-material/Lock';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';

const ColorfulMenuButton = styled(ColorfulButton)`
  padding: 0.5em;
  min-width: auto;
`;

const PostMenu = ({ postId }) => {

  const dispatch = useDispatch();
  const confirm = useConfirm();

  const { t } = useTranslation(['component', 'common']);

  const menuButton = useRef();

  const [menuOpen, setMenuOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editOnArrival, setEditOnArrival] = useRouterLocationState('editPostId', null);

  const post = useSelector(state => postSelectors.getPost(state, postId));
  const poll = useSelector(state => pollSelectors.getPoll(state, post.poll));

  const { isImportant, isCompact, isPinned, isPinnable, isFollowing, hasMenu, pollDataRevealed, setPollDataRevealed } = useContext(PostContext);

  useEffect(() => {
    if (editOnArrival !== postId) {
      return;
    }

    setEditOpen(true);
    setEditOnArrival(null);
  }, [editOnArrival, postId, setEditOnArrival]);

  if (!post || !post.permissions || !hasMenu) {
    return null;
  }

  const canToggleFollow = post.permissions.follow;
  const canTogglePin = post.permissions.pin && isPinnable;
  const canToggleImportant = post.permissions.important;
  const canDownloadVotes = post.permissions.poll.downloadVotes && !isMobile;
  const pollPermissions = post.permissions.poll;
  const canRevealPollData = !!post.poll && (
    (pollPermissions.viewVotes && poll?.hideVotes)
    || (pollPermissions.viewVoters && poll?.anonymous)
    || (pollPermissions.viewAddedBy && poll?.hideAddedBy)
  );
  const canUpdate = post.permissions.update;
  const canCopy = post.permissions.copy;
  const canDelete = post.permissions.delete;
  const canShareToNetwork = post.permissions.shareToNetwork;
  const isSharedToNetwork = post.sharedToNetwork;
  const canToggleClosePoll = !!post.poll && post.permissions.poll.update;
  const { isDraft } = post;

  const onDeleteClick = () => {
    setMenuOpen(false);
    confirm({
      title: t('deletePostDialog.title'),
      description: t('deletePostDialog.info'),
      submit: t('deletePostDialog.deleteButton'),
      cancel: t('deletePostDialog.cancelButton'),
    })
      .then(() => dispatch(postActions.delete({ postId })))
      .catch(() => {});
  };

  const onEditClick = () => {
    setMenuOpen(false);
    if (isMobile) {
      history.push(`/post/${postId}/edit`);
    } else {
      setEditOpen(true);
    }
  };

  const onCopyClick = () => {
    setMenuOpen(false);
    dispatch(postActions.copy({ postId }));
  };

  const onPinClick = () => {
    setMenuOpen(false);
    dispatch(postActions.patch({ postId, pinned: !isPinned }));
  };

  const onFollowClick = () => {
    const action = isFollowing ? postActions.unfollow : postActions.follow;

    dispatch(action({ postId }));
  };

  let postUrl = null;
  try {
    const relativePath = `/post/${postId}`;
    postUrl = new URL(relativePath, config.appUrl).href;
  } catch (e) { } // eslint-disable-line no-empty

  const copyPostLink = () => {
    setMenuOpen(false);
    copyToClipboard(postUrl)
      .then(() => dispatch(snackbarActions.createSuccess(t('component:copyToClipboardAdornment.copied'))))
      .catch((err) => dispatch(snackbarActions.createFailure(err?.message)));
  };

  const onImportantClick = () => {
    setMenuOpen(false);
    dispatch(postActions.patch({ postId, important: !isImportant }));
  };

  const onShareToNetworkClick = () => {
    setMenuOpen(false);
    confirm({
      title: t('common:managePosts.network.shareConfirmation.title'),
      description: t('common:managePosts.network.shareConfirmation.description'),
      submit: t('common:managePosts.network.shareConfirmation.submit'),
      cancel: t('common:managePosts.network.shareConfirmation.cancel'),
    })
      .then(() => dispatch(postActions.shareToNetwork({ postId })))
      .catch(() => {});
  };

  const onPublishClick = () => {
    setMenuOpen(false);
    dispatch(postActions.patch({ postId, isDraft: false }));
  };

  const onDownloadClick = () => {
    setMenuOpen(false);
    dispatch(pollActions.downloadVotes({ pollId: post.poll }));
  };

  const onRevealClick = () => {
    setMenuOpen(false);
    setPollDataRevealed(!pollDataRevealed);
  };

  const onToggleClosePoll = () => {
    setMenuOpen(false);
    const dialogKey = poll.closed ? 'openPollDialog' : 'closePollDialog';
    confirm({
      title: t(`${dialogKey}.title`),
      description: t(`${dialogKey}.info`),
      submit: t(`${dialogKey}.submitButton`),
      cancel: t(`${dialogKey}.cancelButton`),
    })
      .then(() => dispatch(postActions.patch({ postId, poll: { closeAt: poll.closed ? null : new Date() } })))
      .catch(() => {});
  };

  return (
    <Grid item>
      <ColorfulMenuButton
        onClick={() => setMenuOpen(true)}
        ref={menuButton}
        darker={isImportant && isCompact}
        aria-label={t('postActions.menu')}
        aria-haspopup="true"
        aria-controls={menuOpen ? `post-footer-menu-${post.id}` : undefined}
      >
        <MoreVertIcon />
      </ColorfulMenuButton>
      <Menu
        id={`post-footer-menu-${post.id}`}
        open={menuOpen}
        onClose={() => setMenuOpen(false)}
        anchorEl={menuButton.current}
        MenuListProps={{ dense: true }}
      >
        {canUpdate && (
          <MenuItem onClick={onEditClick}>
            <ListItemIcon>
              <EditIcon />
            </ListItemIcon>
            <ListItemText primary={t('postActions.edit')} />
          </MenuItem>
        )}

        {isDraft && (
          <MenuItem onClick={onPublishClick}>
            <ListItemIcon>
              <PublishedWithChangesRoundedIcon />
            </ListItemIcon>
            <ListItemText primary={t('postActions.publishNow')} />
          </MenuItem>
        )}

        {canToggleImportant && (
          <MenuItem onClick={onImportantClick}>
            <ListItemIcon>{isImportant ? <ReportOffRoundedIcon /> : <ErrorIcon />}</ListItemIcon>
            <ListItemText primary={t(isImportant ? 'postActions.removeImportant' : 'postActions.addImportant')} />
          </MenuItem>
        )}

        {canTogglePin && (
          <MenuItem onClick={onPinClick}>
            <ListItemIcon>{isPinned ? <UnpinIcon /> : <PinIcon />}</ListItemIcon>
            <ListItemText primary={t(isPinned ? 'postActions.unpin' : 'postActions.pin')} />
          </MenuItem>
        )}

        {canCopy && (
          <MenuItem onClick={onCopyClick}>
            <ListItemIcon>
              <ContentCopyRoundedIcon />
            </ListItemIcon>
            <ListItemText primary={t('postActions.copyAsDraft')} />
          </MenuItem>
        )}

        <MenuDivider />

        {canToggleClosePoll && (
          <MenuItem onClick={onToggleClosePoll}>
            <ListItemIcon>{poll.closed ? <LockOpenIcon /> : <LockIcon />}</ListItemIcon>
            <ListItemText
              primary={poll.closed ? t('postActions.openPoll') : t('postActions.closePoll')}
            />
          </MenuItem>
        )}

        {canRevealPollData && (
          <MenuItem onClick={onRevealClick}>
            <ListItemIcon>
              {pollDataRevealed ? <VisibilityOffIcon /> : <VisibilityIcon />}
            </ListItemIcon>
            <ListItemText primary={t(pollDataRevealed ? 'postActions.hidePollData' : 'postActions.revealPollData')} />
          </MenuItem>
        )}

        {canDownloadVotes && (
          <MenuItem onClick={onDownloadClick}>
            <ListItemIcon>
              <DownloadIcon />
            </ListItemIcon>
            <ListItemText primary={t('postActions.downloadPollResponses')} />
          </MenuItem>
        )}

        <MenuDivider />

        {canToggleFollow && (
          <MenuItem onClick={onFollowClick}>
            <ListItemIcon>
              {isFollowing ? <NotificationsOffRoundedIcon /> : <NotificationsNoneRoundedIcon />}
            </ListItemIcon>
            <ListItemText
              primary={t(isFollowing ? 'postActions.unfollow' : 'postActions.follow')}
              secondary={t(isFollowing ? 'postActions.unfollowSecondary' : 'postActions.followSecondary')}
            />
          </MenuItem>
        )}

        {postUrl && (
          <MenuItem onClick={copyPostLink}>
            <ListItemIcon>
              <LinkRoundedIcon />
            </ListItemIcon>
            <ListItemText primary={t('postActions.copyLink')} />
          </MenuItem>
        )}

        <MenuDivider />

        {canShareToNetwork && (
          <MenuItem onClick={onShareToNetworkClick}>
            <ListItemIcon>
              <ShareRoundedIcon />
            </ListItemIcon>
            <ListItemText
              primary={t('common:managePosts.network.shareToNetwork')}
              secondary={isSharedToNetwork ? t('common:managePosts.network.alreadyShared') : undefined}
            />
          </MenuItem>
        )}

        <MenuDivider />

        {canDelete && (
          <MenuItem onClick={onDeleteClick}>
            <ListItemIcon>
              <TrashIcon />
            </ListItemIcon>
            <ListItemText primary={t('postActions.delete')} />
          </MenuItem>
        )}
      </Menu>
      <EditPostDialog open={editOpen} onClose={() => setEditOpen(false)} postId={postId} />
    </Grid>
  );
};

PostMenu.propTypes = {
  postId: PropTypes.any,
};

export default PostMenu;
